import {
  TaskCalendarView,
  TaskListMode,
} from "@jugl-web/domain-resources/tasks";
import { TaskPropertiesPanelConfig } from "@jugl-web/domain-resources/tasks/components/TaskPropertiesPanel/types";
import { TaskFormState } from "@jugl-web/domain-resources/tasks/hooks/useTaskFormState";
import { HookOutOfContextError } from "@jugl-web/utils";
import {
  useLocalStorage,
  useSessionStorage,
} from "@jugl-web/utils/hooks/useStorage";
import {
  TASK_CALENDAR_DATE_KEY,
  TASK_CALENDAR_VIEW_KEY,
  TASK_LIST_LAYOUT_KEY,
  TASK_LIST_MODE_KEY,
} from "@jugl-web/utils/storage";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type ViewMode =
  | "date-view"
  | "labels-view"
  | "priority-view"
  | "status-view"
  | "custom-field-view";

export type SupportedTaskPropertiesPanelConfigOverrides = {
  dueDate?: Partial<TaskPropertiesPanelConfig["dueDate"]>;
  assignees?: Partial<TaskPropertiesPanelConfig["assignees"]>;
};

export interface NewTaskDialogState {
  isOpen: boolean;
  initialFormState?: Partial<TaskFormState>;
  taskPropertiesPanelConfigOverrides?: SupportedTaskPropertiesPanelConfigOverrides;
}

export type TaskListLayout = "kanban" | "table" | "calendar";

interface TasksPageContextValue {
  newTaskDialogState: NewTaskDialogState;
  taskListMode: TaskListMode;
  taskListLayout: TaskListLayout;
  taskCalendarView: TaskCalendarView;
  taskCalendarDate: Date;
  setTaskListMode: (mode: TaskListMode) => void;
  setTaskListLayout: (layout: TaskListLayout) => void;
  setTaskCalendarView: (view: TaskCalendarView) => void;
  setTaskCalendarDate: (date: Date) => void;
  openNewTaskDialog: (state: Omit<NewTaskDialogState, "isOpen">) => void;
  closeNewTaskDialog: () => void;
}

export const TasksPageContext = createContext<TasksPageContextValue | null>(
  null
);

interface TasksPageContextProviderProps {
  children: ReactNode;
}

export const TasksPageContextProvider: FC<TasksPageContextProviderProps> = ({
  children,
}) => {
  const [newTaskDialogState, setNewTaskDialogState] =
    useState<NewTaskDialogState>({ isOpen: false });

  const [taskListMode, setTaskListMode] = useLocalStorage<TaskListMode>(
    TASK_LIST_MODE_KEY,
    "personal"
  );

  const [taskListLayout, setTaskListLayout] = useLocalStorage<TaskListLayout>(
    TASK_LIST_LAYOUT_KEY,
    "kanban"
  );

  const [taskCalendarView, setTaskCalendarView] =
    useLocalStorage<TaskCalendarView>(TASK_CALENDAR_VIEW_KEY, "week");

  const modeBasedTaskCalendarView = useMemo<TaskCalendarView>(() => {
    if (taskListMode === "team") {
      return "week";
    }

    return taskCalendarView;
  }, [taskCalendarView, taskListMode]);

  const [taskCalendarDateString, setTaskCalendarDateString] =
    useSessionStorage<string>(TASK_CALENDAR_DATE_KEY, new Date().toJSON());

  const taskCalendarDate = useMemo(
    () => new Date(taskCalendarDateString),
    [taskCalendarDateString]
  );

  const setTaskCalendarDate = useCallback(
    (date: Date) => setTaskCalendarDateString(date.toJSON()),
    [setTaskCalendarDateString]
  );

  const openNewTaskDialog = (state: Omit<NewTaskDialogState, "isOpen">) => {
    setNewTaskDialogState({ isOpen: true, ...state });
  };

  const closeNewTaskDialog = () => {
    setNewTaskDialogState((previousState) => ({
      ...previousState,
      isOpen: false,
    }));
  };

  const contextValue = useMemo<TasksPageContextValue>(
    () => ({
      newTaskDialogState,
      taskListMode,
      taskListLayout,
      taskCalendarView: modeBasedTaskCalendarView,
      taskCalendarDate,
      setTaskListMode,
      setTaskListLayout,
      setTaskCalendarView,
      setTaskCalendarDate,
      openNewTaskDialog,
      closeNewTaskDialog,
    }),
    [
      newTaskDialogState,
      taskListLayout,
      taskListMode,
      modeBasedTaskCalendarView,
      taskCalendarDate,
      setTaskListLayout,
      setTaskListMode,
      setTaskCalendarView,
      setTaskCalendarDate,
    ]
  );

  return (
    <TasksPageContext.Provider value={contextValue}>
      {children}
    </TasksPageContext.Provider>
  );
};

export const useTasksPageContext = () => {
  const context = useContext(TasksPageContext);

  if (!context) {
    throw new HookOutOfContextError("useTasksPageContext", "TasksPageContext");
  }

  return context;
};
